import { useEffect } from 'react';
import useGlobalContext from "../../context/useGlobalContext.ts";
import useDisplaySnackbar from '../../utils/useDisplaySnackbar.tsx';
import React, { useState } from 'react';
import { getColumns, initialSort } from "../Meetings/meetingsTableConfig.tsx";
import {convertToUTC, validateDate} from "../../utils/utils.ts"
import UserInteractionsService from '../../services/interactions/Interactions.ts';
import Datatable from "../Common/Datatable.tsx";
import { Box, CircularProgress } from '@mui/material';

const generateRows = (meetings: any, isTeamMeeting: boolean,
    parent_page_identifier: any, selectedTab: any,
    isSlideOverOpen?: boolean | undefined,
    slideOverParent?: string,
    isClientInteraction?:boolean,
    userEmail?: string,
    profileOrgId?: string,
    )=>
    meetings.map((item: any) => ({
        id: item.interactionId,
        title: item.interactionTitle || "",
        description: item.interactionDescription || "",
        start_time: item.startTime, 
        end_time: item.endTime,
        duration: item.duration,
        no_of_attendees: item.noOfAttendees,
        is_external: item.isExternal, 
        attendees: item.attendees || {},
        joinLink: item.joinLink || "", 
        meeting_sentiment: item.overallSentiment || null,
        rating_source: item.ratingSource || null,
        isTeamMeeting: isTeamMeeting,
        parent_page_identifier: parent_page_identifier,
        selectedTab: selectedTab,
        isSlideOverOpen: isSlideOverOpen,
        slideOverParent: slideOverParent,
        isClientInteraction: isClientInteraction,
        userEmail: userEmail,
        profileOrgId: profileOrgId,
        sentimentSummary: item.sentimentSummary,
        ...(() => {
      const list = [...(item.accountOpportunities || [])].sort(
        (a: any, b: any) => (b?.opportunityName?.trim() ? 1 : 0) - (a?.opportunityName?.trim() ? 1 : 0)
      );
      const accs = Array.from(new Set(list.map((o: any) => o.accountName?.trim()).filter(Boolean)));
      const opps = Array.from(new Set(list.map((o: any) => o.opportunityName?.trim()).filter(Boolean)));
      return {
        account: accs,
        opportunityName: opps,
      };
    })(),
    }));

    const ContactsInteraction: React.FC<{ userEmail: string,  startTime?: string;
    endTime?: string; employee_email_ids?: string; parent_page_identifier?: string; 
     selectedTab?: any, isSlideOverOpen?: boolean | undefined; slideOverParent?: string; 
     isClientInteraction?: boolean; profileOrgId?: string;
     }> = ({userEmail, parent_page_identifier, selectedTab, 
        isSlideOverOpen, slideOverParent, isClientInteraction, profileOrgId,
    }) => {
    const [rows, setRows] = useState<any[]>([]);
    const [totalRecord, setTotalRecord] = useState(0);
    const { calendar, startEndTime, timeRange } = useGlobalContext();
    const { showSnackbar } = useDisplaySnackbar();
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [sort, setSort] = useState(initialSort);
    const { startEndMeetingTime } = useGlobalContext()
    const [columns, setColumns] = useState<any[]>([]);

        useEffect(() => {
        const baseCols = getColumns();

        const updatedCols = baseCols.map((col: any) => {
            if (col.accessorKey === "start_time") {
            return {
                ...col,
                Cell: ({ cell }: any) => {
                const value = cell.getValue();
                const isUpcoming = new Date(value) > new Date();

                return (
                    <span
                    style={{
                        color: isUpcoming ? "#bd00cd" : "inherit",
                        fontWeight: isUpcoming ? 600 : "normal",
                    }}
                    >
                    {validateDate(value, "MMM DD, YYYY hh:mm A")}
                    </span>
                );
                },
            };
            }
            return col;
        });

        setColumns(updatedCols);
        }, []);


    const {
        isLoading: isInteractionsLoading,
        isFetching: isInteractionsFetching,
        isSuccess: isInteractionsSuccess,
        isError: isInteractionsError,
        error: interactionsError,
        data: totalInteractionsData,
      } = UserInteractionsService.useGetUserInteractions(
        {
          startTime: convertToUTC(startEndMeetingTime?.start_time),
          endTime: convertToUTC(startEndMeetingTime?.end_time),
          offset: pageNumber,
          limit: pageSize,
          sortBy: sort.sort_field,
          sortOrder: sort.sort_order,
          isTeamInteraction: false,
          isOnlyInteraction: true,
          isTeamOnlyInteraction: false,
          employeePorId: profileOrgId || "",
          isSupportAdditionalCols: true,
        },
        !!isSlideOverOpen && !!userEmail,
      );

      const {
          isLoading: isCountLoading,
          isFetching: isCountFetching,
          isSuccess: isCountSuccess,
          data: countData,
        } = UserInteractionsService.useGetUserInteractionCount(
          {
            startTime: convertToUTC(startEndMeetingTime?.start_time),
            endTime: convertToUTC(startEndMeetingTime?.end_time),
            employeePorId: profileOrgId || "",
            isTeamOnlyInteraction: true,
            isTeamInteraction: false,
            isOnlyInteraction: true,
          },
          !!isSlideOverOpen && !!userEmail,
        );

        useEffect(() => {
            setShowSpinner(isCountLoading || isCountFetching)
            if(isCountSuccess){
              setTotalRecord(countData?.totalCount);
            }
          },[isCountSuccess, countData])
      
      useEffect(()=>{
        setShowSpinner(isInteractionsLoading || isInteractionsFetching);
        if (isInteractionsSuccess && totalInteractionsData) {
            const totalInteractionList = totalInteractionsData?.data || [];
            setTotalRecord(countData?.totalCount);
            if (totalInteractionList.length > 0) {
                setRows(generateRows(totalInteractionList, true, parent_page_identifier, selectedTab,
                     isSlideOverOpen, slideOverParent, isClientInteraction, userEmail, profileOrgId));
            } else {
                setRows([]);
            }
        }
        if (isInteractionsError) {
            showSnackbar("error", interactionsError || "Failed to fetch client interactions");
        }

      }, [isInteractionsLoading, isInteractionsFetching, interactionsError, isInteractionsSuccess, isInteractionsError, totalInteractionsData])


    const onPageChange = (value: number) => {
        setPageNumber(value);
    };

    const onPaginationChange = (pageNumber: number) => {
        onPageChange(pageNumber + 1);
    };


    const onSortingChange = (sorting: any[]) => {
        if (sorting && sorting.length > 0)
            setSort({
                sort_field: sorting[0].id,
                sort_order: sorting[0].desc === true ? "desc" : "asc",
            });
    };
    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
    };
    
    return (
        <>
            <Box
                className="relative flex flex-col justify-start items-start w-full"
                sx={{ gap: "12px" }}
            >
                {showSpinner && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50 z-10">
                    <CircularProgress sx={{ color: "#ccc", opacity: 0.5  }} thickness={5} size={60} />
                </div>
                )}
                <Box className="w-full">
                    <Datatable
                        columns={columns}
                        rows={rows}
                        rowCount={totalRecord}
                        pageSize={pageSize}
                        initialSort={sort}
                        onPaginationChange={onPaginationChange}
                        onSortingChange={onSortingChange}
                        startEndTime={startEndTime}
                        timeRange={timeRange}
                        calendarId={calendar?.calendar_id}
                        onPageSizeChange={(newPageSize: number | null | undefined) => {
                            if (newPageSize !== null && newPageSize !== undefined) {
                              handlePageSizeChange(newPageSize);
                            }
                        }}
                        columnVisibility={{is_manager: false, meeting_sentiment: false, rating_source: false, duration: false,actual_duration: false,
                             sharedDate: false, abstractiveScore: false, shareIcon: false, opportunityName: false, momDocLink: false, talkTrack: false}}
                        maxHeight="40vh"
                    />
                </Box>
            </Box>
        </>
    );
};

export default ContactsInteraction;